/**
 * Booking API Service
 *
 * Handles all API calls for workshop bookings:
 * - CRUD operations
 * - Calendar view queries (day/week/month)
 * - Rescheduling (drag-and-drop)
 * - Status updates
 * - Vehicle received (with optional loan car handover details)
 * - Shared metadata (category types)
 * - Service Profiles (for booking form picker and step 3 summary)
 *
 * Loan car photo upload, image categories, and incident history have moved
 * to loanCarIncidentApi.js — all calls now go through /loan-car-incidents.
 */

import axiosInstance from './axiosInstance';

// ============================================
// CRUD Operations
// ============================================

export const getBookingsApi = async () => {
    const response = await axiosInstance.get('/bookings');
    return response.data;
};

export const getBookingsPaginatedApi = async ({ page = 0, size = 10 } = {}) => {
    const response = await axiosInstance.get('/bookings/paginated', {
        params: { page, size }
    });
    return response.data;
};

export const getBookingApi = async (uuid) => {
    const response = await axiosInstance.get(`/bookings/${uuid}`);
    return response.data;
};

/**
 * Create an ad-hoc (on-demand) booking.
 * Hits POST /bookings.
 *
 * Body shape:
 * {
 *   scheduledDate:            string       // YYYY-MM-DD, required
 *   scheduledTime:            string|null  // HH:MM, optional
 *   serviceTypes:             [             // required, at least one entry
 *     {
 *       type:            string,           // BookingServiceCategory enum value, required
 *       durationMinutes: number|null,      // per-row estimate
 *       description:     string|null,      // per-row notes
 *     }
 *   ],
 *   estimatedDurationMinutes: number|null  // sum of per-row durations (FE-computed)
 *   description:              string|null  // booking-level notes
 *   customer:                 { uuid } | { name, phone, email }
 *   vehicle:                  { uuid } | { rego, regoState, make, model, year }
 *   odometerIn:               number|null  // mandatory on create flow (frontend gate)
 *
 *   // ── Optional handover / pickup person details (all nullable) ──
 *   handoverPersonName:       string|null  // who is dropping the vehicle off
 *   handoverPersonMobile:     string|null  // their mobile (no AU-format check)
 *   pickupPersonName:         string|null  // who will collect after service
 *   pickupPersonMobile:       string|null  // their mobile (no AU-format check)
 * }
 *
 * Ownership transfer: if the supplied rego is already on file under a
 * different customer, the backend silently re-points Vehicle.customer to
 * the supplied customer. The frontend shows an informational warning on
 * Step 2 of the booking wizard ahead of time so staff know what's happening
 * — see checkVehicleOwnershipApi below.
 */
export const createBookingApi = async (data) => {
    const response = await axiosInstance.post('/bookings', data);
    return response.data;
};

/**
 * Create a booking from a ServiceProfile.
 * Hits POST /bookings/from-service-profile.
 *
 * Body shape (mirrors BookingService.CreateServiceProfileBookingRequest):
 * {
 *   scheduledDate:      string       // YYYY-MM-DD, required
 *   scheduledTime:      string|null  // HH:MM, optional
 *   serviceProfileUuid: string       // required
 *   description:        string|null  // optional notes
 *   customer:           { uuid } | { name, phone, email }
 *   vehicle:            { uuid } | { rego, regoState, make, model, year }
 *
 *   // ── Optional handover / pickup person details (all nullable) ──
 *   handoverPersonName:   string|null
 *   handoverPersonMobile: string|null
 *   pickupPersonName:     string|null
 *   pickupPersonMobile:   string|null
 * }
 *
 * serviceTypes and estimatedDurationMinutes are intentionally omitted —
 * they are derived server-side from the profile's work items.
 */
export const createServiceProfileBookingApi = async (data) => {
    const response = await axiosInstance.post('/bookings/from-service-profile', data);
    return response.data;
};

/**
 * Update an existing booking.
 * Hits PUT /bookings/{uuid}.
 *
 * Body shape:
 * {
 *   scheduledDate:            string       // YYYY-MM-DD, required
 *   scheduledTime:            string|null  // HH:MM, optional
 *   serviceTypes:             [...]        // same per-row shape as create
 *   description:              string|null
 *   estimatedDurationMinutes: number|null
 *   customer: { uuid },                     // UUID-only on update
 *   vehicle:  { uuid },                     // UUID-only on update
 *
 *   // ── Optional handover / pickup person details (all nullable) ──
 *   // Sending an empty string or null clears the previously-stored value.
 *   handoverPersonName:   string|null
 *   handoverPersonMobile: string|null
 *   pickupPersonName:     string|null
 *   pickupPersonMobile:   string|null
 * }
 */
export const updateBookingApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/bookings/${uuid}`, data);
    return response.data;
};

export const cancelBookingApi = async (uuid) => {
    const response = await axiosInstance.delete(`/bookings/${uuid}`);
    return response.data;
};

// ============================================
// Rescheduling (Drag-and-Drop)
// ============================================

export const rescheduleBookingApi = async (uuid, newDate, newTime) => {
    const response = await axiosInstance.patch(`/bookings/${uuid}/reschedule`, {
        newDate,
        newTime
    });
    return response.data;
};

// ============================================
// Status Updates
// ============================================

/**
 * Generic status update — for all statuses EXCEPT VEHICLE_RECEIVED.
 * Hits PATCH /bookings/{uuid}/status.
 *
 * For VEHICLE_RECEIVED use markVehicleReceivedApi() below, which carries
 * optional loan car handover details.
 */
export const updateBookingStatusApi = async (uuid, status) => {
    const response = await axiosInstance.patch(`/bookings/${uuid}/status`, { status });
    return response.data;
};

/**
 * Transition a booking to VEHICLE_RECEIVED.
 * Hits PATCH /bookings/{uuid}/vehicle-received.
 *
 * Body shape:
 * {
 *   loanCarDetails: {
 *     odometerReading: number | null,
 *     insuranceTaken:  boolean,
 *     notes:           string | null,
 *     imagesByCategory: [
 *       {
 *         category: string,           // DRIVING_LICENCE | CAR_DASHBOARD_ON_VEHICLE_START | ...
 *         images:   [{ s3Key: string }]  // keys from uploadLoanCarPhotoApi
 *       }
 *     ]
 *   } | null   // null when the booking has no loan car
 * }
 *
 * The backend enforces that loanCarDetails is non-null when an active
 * allocation exists — the frontend passes null when there is no loan car.
 * Photos must be uploaded first via loanCarIncidentApi.uploadLoanCarPhotoApi.
 */
export const markVehicleReceivedApi = async (uuid, loanCarDetails = null, customerEmail = null, customerName = null) => {
    const response = await axiosInstance.patch(`/bookings/${uuid}/vehicle-received`, {
        loanCarDetails,
        // Only ever set for a customer who has none — bookings taken by the AI phone bot
        // deliberately skip email capture, so it happens at the counter instead. The
        // backend ignores it when the customer already has an address.
        customerEmail,
        customerName,
    });
    return response.data;
};

// ============================================
// Vehicle Update
// ============================================

/**
 * Fetch a vehicle's full details by rego number.
 * Hits GET /vehicles/by-rego/{rego}.
 * Used to populate the edit form when staff click the pencil icon.
 */
export const getVehicleByRegoApi = async (rego) => {
    const response = await axiosInstance.get(`/vehicles/by-rego/${encodeURIComponent(rego)}`);
    return response.data;
};

/**
 * Update an existing vehicle's details.
 * Hits PUT /vehicles/{uuid}.
 *
 * Body shape:
 * {
 *   rego:         string|null,
 *   regoState:    string|null,
 *   make:         string|null,
 *   model:        string|null,
 *   year:         number|null,
 *   vin:          string|null,
 *   engineNumber: string|null,
 * }
 */
export const updateVehicleApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/vehicles/${uuid}`, data);
    return response.data;
};

/**
 * Delete a vehicle and its service history. Hits DELETE /vehicles/{uuid}.
 *
 * Requires the `vehicle:delete` permission — the server rejects with 403
 * otherwise, so hiding the button is presentation only, never the control.
 *
 * Rejects with 400 if the vehicle still has bookings or jobs; the message names
 * how many of each, so surface `error.response.data.message` verbatim rather
 * than a generic failure string.
 *
 * @returns {Promise<{ uuid: string, serviceRecordsDeleted: number }>}
 */
export const deleteVehicleApi = async (uuid) => {
    const response = await axiosInstance.delete(`/vehicles/${uuid}`);
    return response.data;
};

/**
 * Check whether a typed rego is already on file under a *different* customer.
 * Hits POST /vehicles/check-ownership.
 *
 * Called from Step 2 of the booking wizard once the user has typed a valid
 * rego AND we have at least one of name / phone / email for the customer
 * (whether from a Step 1 lookup or freshly typed into the new-customer form).
 *
 * Purely advisory — when the booking is later saved with a rego that exists
 * under a different customer, BookingService.resolveOrCreateVehicle silently
 * re-points the vehicle's customer FK. This endpoint just lets the FE warn
 * the user *before* they hit Continue so they understand what's about to
 * happen.
 *
 * Request body:
 * {
 *   rego:          string,            // required
 *   customerName:  string|null,       // at least one of these three must
 *   customerPhone: string|null,       // be non-blank — backend returns 400
 *   customerEmail: string|null,       // otherwise (controller validation)
 * }
 *
 * Response:
 * {
 *   vehicleExists:        boolean,
 *   ownershipConflict:    boolean,    // true only when vehicle exists AND
 *                                     // NONE of name/phone/email match
 *   vehicleUuid:          string|null,// when found
 *   existingCustomerUuid: string|null,
 *   existingCustomerName: string|null,
 *   existingCustomerPhone:string|null,
 *   existingCustomerEmail:string|null,
 *   vehicleDescription:   string|null // "2018 Toyota Corolla" or rego fallback
 * }
 *
 * POST (not GET) so the customer contact details travel in the body rather
 * than the URL — keeps them out of access logs and browser history.
 * No side effects on the backend — purely a read.
 */
export const checkVehicleOwnershipApi = async ({ rego, customerName, customerPhone, customerEmail }) => {
    const response = await axiosInstance.post('/vehicles/check-ownership', {
        rego,
        customerName:  customerName  || null,
        customerPhone: customerPhone || null,
        customerEmail: customerEmail || null,
    });
    return response.data;
};

// ============================================
// Calendar View Queries
// ============================================

export const getBookingsByDateApi = async (date) => {
    const response = await axiosInstance.get(`/bookings/date/${date}`);
    return response.data;
};

export const getBookingsByDateRangeApi = async (startDate, endDate) => {
    const response = await axiosInstance.get('/bookings/range', {
        params: { startDate, endDate }
    });
    return response.data;
};

export const getBookingsForWeekApi = async (weekStart) => {
    const response = await axiosInstance.get('/bookings/week', {
        params: { weekStart }
    });
    return response.data;
};

export const getBookingsForMonthApi = async (year, month) => {
    const response = await axiosInstance.get('/bookings/month', {
        params: { year, month }
    });
    return response.data;
};

export const getUpcomingBookingsApi = async () => {
    const response = await axiosInstance.get('/bookings/upcoming');
    return response.data;
};

// ============================================
// Filter Queries
// ============================================

export const getBookingsByCustomerApi = async (customerId) => {
    const response = await axiosInstance.get(`/bookings/customer/${customerId}`);
    return response.data;
};

export const getBookingsByVehicleApi = async (vehicleId) => {
    const response = await axiosInstance.get(`/bookings/vehicle/${vehicleId}`);
    return response.data;
};

// ============================================
// Summary
// ============================================

export const getBookingSummaryApi = async () => {
    const response = await axiosInstance.get('/bookings/summary');
    return response.data;
};

export const getDateSummaryApi = async (date) => {
    const response = await axiosInstance.get(`/bookings/summary/date/${date}`);
    return response.data;
};

// ============================================
// Metadata / Enums
// ============================================

/**
 * Get all BookingServiceCategory.ServiceCategory enum values.
 *
 * Powers the Service Types multi-select picker in the New / Edit Booking form
 * with the high-level booking-time categories (Major Service, Minor Service,
 * Log Book Service, Technical Issue, Roadworthy Certificate).
 *
 * The fine-grained WorkItemCategory.CategoryType enum is a separate concern
 * used on job work items, served by /jobs/meta/category-types — not consumed
 * by booking flows.
 *
 * Returns [{ value, label, description }]
 */
export const getBookingServiceCategoriesApi = async () => {
    const response = await axiosInstance.get('/bookings/meta/service-categories');
    return response.data;
};

// ============================================
// Service Profiles
// ============================================

/**
 * Fetch active service profiles for the booking form picker (Step 2).
 * Loads the full list in one shot (size=200) so text search is in-memory.
 * Returns a Page<ServiceProfileResponse>; caller unpacks .content.
 */
/**
 * What a vehicle is due at a given odometer reading, before anything is booked.
 *
 * Read-only. Returns the baseline it measured from, the services that came out due with
 * the reason for each, and the merged work items — so a service advisor can check the
 * reading against the history before quoting a customer on it.
 */
export const previewServiceDueApi = async ({ vehicleUuid, odometerKm }) => {
    const response = await axiosInstance.get('/service-due/preview', {
        params: { vehicleUuid, odometerKm },
    });
    return response.data;
};

/**
 * Creates a booking whose work is worked out from an odometer reading rather than
 * chosen from a profile. Same request shape as the profile route — see the backend's
 * CreateServiceProfileBookingRequestDto, which both share deliberately.
 */
export const createBookingFromMileageApi = async (payload) => {
    const response = await axiosInstance.post('/bookings/from-mileage', payload);
    return response.data;
};

export const getActiveServiceProfilesApi = async () => {
    const response = await axiosInstance.get('/service-profiles', {
        params: { activeOnly: true, size: 200, page: 0 }
    });
    return response.data;
};

/**
 * Fetch a single service profile by UUID (Step 3 summary).
 * Returns the full ServiceProfileResponse including nested workItems,
 * parts per work item, labour cost, and estimated hours.
 */
export const getServiceProfileApi = async (uuid) => {
    const response = await axiosInstance.get(`/service-profiles/${uuid}`);
    return response.data;
};

// ============================================
// Default Export
// ============================================

const bookingApi = {
    // CRUD
    getBookingsApi,
    getBookingsPaginatedApi,
    getBookingApi,
    createBookingApi,
    createServiceProfileBookingApi,
    updateBookingApi,
    cancelBookingApi,
    // Reschedule
    rescheduleBookingApi,
    // Status
    updateBookingStatusApi,
    markVehicleReceivedApi,
    // Vehicle
    getVehicleByRegoApi,
    updateVehicleApi,
    deleteVehicleApi,
    checkVehicleOwnershipApi,
    // Calendar Views
    getBookingsByDateApi,
    getBookingsByDateRangeApi,
    getBookingsForWeekApi,
    getBookingsForMonthApi,
    getUpcomingBookingsApi,
    // Filters
    getBookingsByCustomerApi,
    getBookingsByVehicleApi,
    // Summary
    getBookingSummaryApi,
    getDateSummaryApi,
    // Metadata
    getBookingServiceCategoriesApi,
    // Service Profiles
    getActiveServiceProfilesApi,
    getServiceProfileApi,
};

export default bookingApi;